/**
 * Klantvriendelijk overzicht: alleen betaalmethoden die we daadwerkelijk aanbieden.
 */
import { Link } from 'react-router-dom';
import { CreditCard, ShieldCheck } from 'lucide-react';
import { AnimatedSection } from '../components/AnimatedSection';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { PaymentBrandIcon } from '../components/payment/AcceptedPaymentBrands';
import { SeoMeta } from '../components/SeoMeta';
import { CUSTOMER_BRAND_ORDER, CUSTOMER_METHOD_COPY } from '../data/customer-payment-methods';

export function PaymentMethods() {
  return (
    <div className="animate-page-enter">
      <SeoMeta
        path="/betaalmethoden"
        title="Betaalmethoden — veilig betalen"
        description="Betaal je CalqTool™-abonnement met iDEAL, Bancontact, kaarten, Apple Pay, Google Pay, Klarna en meer — veilig via Stripe."
      />
      <Breadcrumbs items={[{ label: 'Home', href: '/' }, { label: 'Betaalmethoden' }]} />

      <header className="mb-10 border-b border-border pb-8">
        <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-sm font-semibold text-primary">
          <CreditCard className="h-4 w-4" aria-hidden />
          Veilig betalen
        </div>
        <h1 className="text-3xl font-bold tracking-tight text-text sm:text-4xl">Zo betaal je</h1>
        <p className="mt-4 max-w-2xl text-lg text-text-muted">
          Abonnementen betaal je via <strong className="font-semibold text-text">Stripe</strong> — versleuteld en zoals je
          gewend bent van grote webshops. Hieronder staan de methoden die je bij ons kunt gebruiken (afhankelijk van je
          bank, apparaat en land kan het aanbod in checkout iets verschillen).
        </p>
        <div className="mt-6 flex flex-wrap items-center gap-3">
          <div className="inline-flex items-center gap-2 rounded-xl border border-border bg-surface-card px-4 py-3 text-sm text-text-muted shadow-sm">
            <ShieldCheck className="h-5 w-5 shrink-0 text-emerald-600 dark:text-emerald-400" aria-hidden />
            <span>PCI-conforme betalingen; wij slaan geen kaartgegevens op.</span>
          </div>
          <Link
            to="/prijzen"
            className="inline-flex items-center rounded-xl border border-border bg-surface-card px-4 py-3 text-sm font-medium text-primary hover:border-primary"
          >
            Naar pakketten en prijzen →
          </Link>
        </div>
      </header>

      <AnimatedSection className="mb-10">
        <h2 className="sr-only">Geaccepteerde betaalmethoden</h2>
        <div className="mb-6 flex flex-wrap gap-2 rounded-2xl border border-border bg-surface-card/80 p-4 shadow-sm sm:p-5">
          {CUSTOMER_BRAND_ORDER.map((id) => (
            <PaymentBrandIcon key={id} brand={id} label={CUSTOMER_METHOD_COPY[id].label} />
          ))}
        </div>
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {CUSTOMER_BRAND_ORDER.map((id) => {
            const { label, blurb } = CUSTOMER_METHOD_COPY[id];
            return (
              <article
                key={id}
                className="flex gap-4 rounded-xl border border-border bg-surface-card p-4 shadow-sm transition hover:border-primary/25 hover:shadow-md"
              >
                <div className="shrink-0 pt-0.5">
                  <PaymentBrandIcon brand={id} label={label} />
                </div>
                <div className="min-w-0">
                  <h3 className="font-semibold text-text">{label}</h3>
                  <p className="mt-1 text-sm leading-relaxed text-text-muted">{blurb}</p>
                </div>
              </article>
            );
          })}
        </div>
      </AnimatedSection>

      <footer className="mt-8 rounded-xl border border-border bg-surface p-5 text-sm text-text-muted">
        <p>
          Welke opties je precies ziet in de betaalstap hangt af van <strong className="text-text">valuta</strong>,{' '}
          <strong className="text-text">land</strong> en je apparaat. Voor juridische voorwaarden zie{' '}
          <Link to="/terms" className="font-medium text-primary hover:underline">
            algemene voorwaarden
          </Link>{' '}
          en{' '}
          <Link to="/privacy" className="font-medium text-primary hover:underline">
            privacy
          </Link>
          .
        </p>
      </footer>
    </div>
  );
}
